import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";
import logo from "../assets/logo_cube.png";
import usericon from "../assets/User_icon.svg";
import profileicon from "../assets/Profile_icon.svg";
import logouticon from "../assets/log_out.svg";
import { performLogout } from '../utils/authUtils';
import { useAuth } from "../context/AuthContext"; // ✨ NEW

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth(); // ✨ Pull user state from RAM

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Derived state from AuthContext
  const isLoggedIn = !!user;
  const userName = user ? user.firstName : "";
  const userAvatar = user?.avatar || usericon;

  const isAdmin = user?.role?.toLowerCase() === "admin";

  const goToAdmin = () => {
    setDropdownOpen(false);
    navigate('/admin');
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "unset";
  };

  const handleLogout = async () => {
    // 1. Tell backend to kill the cookie
    await performLogout(navigate);
    // 2. Tell RAM to forget the user
    logout(); 
    setDropdownOpen(false);
  };

  const goToProfile = () => {
    setDropdownOpen(false);
    navigate('/profile');
  };

 const handleUserAreaClick = () => {
    // Explicitly check the actual user object from RAM
    if (user && isLoggedIn) {
      setDropdownOpen(!dropdownOpen);
    } else {
      // Force dropdown closed to prevent it from getting stuck
      setDropdownOpen(false); 
      navigate('/log-in'); 
    }
  };

  if (location.pathname.includes("/loading")) return null;

  return (
    <nav className={`navbar-wrapper ${isScrolled ? "scrolled" : ""}`}>
      <div className="navbar-content">
        <Link to="/" className="navbar-logo" onClick={() => setIsMenuOpen(false)}>
          <img src={logo} alt="AInt-Reel logo" className="nav-logo-img" />
        </Link>

        <div className="navbar-center desktop-only">
          <div className="nav-links-container">
            <Link to="/scan" className={`nav-link ${location.pathname === "/scan" ? "active" : ""}`}>SCAN</Link>
            <Link to="/how-it-works" className={`nav-link ${location.pathname === "/how-it-works" ? "active" : ""}`}>HOW IT WORKS?</Link>
            <Link to="/ai-awareness" className={`nav-link ${location.pathname === "/ai-awareness" ? "active" : ""}`}>AI AWARENESS</Link>
            <Link to="/faq" className={`nav-link ${location.pathname === "/faq" ? "active" : ""}`}>FAQ</Link>
          </div>
        </div>

        <div className="navbar-right">
          <div 
            className="desktop-only flex-row" 
            onClick={handleUserAreaClick} 
            onMouseLeave={() => setDropdownOpen(false)} 
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
          >
            <span className="login-text">{isLoggedIn ? userName : "Log In"}</span>
            
            <div className="profile-section">
              <img src={userAvatar} style={{ height: "50px", width: "50px", borderRadius: "50%", objectFit: "cover" }} alt="User icon" />
              {isLoggedIn && dropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); goToProfile(); }}>
                    <span>Profile</span>
                    <img src={profileicon} style={{ height: "30px" }} alt="Profile icon" />
                  </div>
                  
                  {/* ✨ STRICT CHECK: Only render for Admin */}
                  {isAdmin && (
                    <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); goToAdmin(); }}>
                      <span>System Settings</span>
                      {/* You can add a gear icon here later if you have one imported */}
                    </div>
                  )}

                  <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>
                    <span>Log out</span>
                    <img src={logouticon} style={{ height: "30px" }} alt="Logout icon" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`hamburger-container ${isMenuOpen ? "open" : ""}`} onClick={toggleMenu}>
            <div className="hamburger-lines">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen && <div className="mobile-backdrop" onClick={toggleMenu}></div>}
      <div className={`mobile-overlay ${isMenuOpen ? "active" : ""}`}>
        <div className="mobile-menu-content">

          <div className="mobile-nav-links">
            <Link to="/scan" className={`mobile-link ${location.pathname === "/scan" ? "m-active" : ""}`} onClick={toggleMenu}>SCAN</Link>
            <Link to="/how-it-works" className={`mobile-link ${location.pathname === "/how-it-works" ? "m-active" : ""}`} onClick={toggleMenu}>HOW IT WORKS</Link>
            <Link to="/ai-awareness" className={`mobile-link ${location.pathname === "/ai-awareness" ? "m-active" : ""}`} onClick={toggleMenu}>AI AWARENESS</Link>
            <Link to="/faq" className={`mobile-link ${location.pathname === "/faq" ? "m-active" : ""}`} onClick={toggleMenu}>FAQ</Link>

           {/* STRICT CHECK: Only show protected routes if user exists */}
            {!(user && isLoggedIn) ? (
              <Link to="/log-in" className={`mobile-link ${location.pathname === "/log-in" ? "m-active" : ""}`} onClick={toggleMenu}>LOGIN</Link>
            ) : (
              <>
                <Link to="/profile" className={`mobile-link ${location.pathname === "/profile" ? "m-active" : ""}`} onClick={toggleMenu}>PROFILE</Link>
                
                {/* ✨ STRICT CHECK: Only render for Admin */}
                {isAdmin && (
                  <Link to="/admin" className={`mobile-link ${location.pathname === "/admin" ? "m-active" : ""}`} onClick={toggleMenu}>SYSTEM SETTINGS</Link>
                )}
              </>
            )}
          </div>

          {/* STRICT CHECK: Hide LOG OUT section completely if user is not fully loaded */}
          {(user && isLoggedIn) && (
            <div className="mobile-logout-section" onClick={handleLogout}>
              <span>LOG OUT</span>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;